export class PluginConfigManager {
    static renderForm(type: string, currentConfig: string): string {
        let config: any = {};
        try {
            config = JSON.parse(currentConfig);
        } catch (e) {
            config = {};
        }

        switch (type) {
            case 'cors':
                return this.renderCorsForm(config);
            case 'api_key_validation':
                return this.renderApiKeyForm(config);
            case 'ip_filter':
                return this.renderIpFilterForm(config);
            case 'payload_size_filter':
                return this.renderPayloadFilterForm(config);
            case 'mock':
                return this.renderMockForm(config);
            default:
                return `
                    <div class="form-config-box">
                        <label class="form-label">Configuration (JSON)</label>
                        <textarea class="form-textarea" name="cfg-json" style="height: 300px;">${JSON.stringify(config, null, 2)}</textarea>
                    </div>
                `;
        }
    }

    private static renderCorsForm(config: any): string {
        const potentialMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS', 'HEAD'];
        const methods = Array.isArray(config.allow_methods) ? config.allow_methods : (config.allow_methods || '').split(',').map((s: string) => s.trim());
        const headers = Array.isArray(config.allow_headers) ? config.allow_headers : (config.allow_headers || '').split(',').map((s: string) => s.trim()).filter((h: string) => h !== '');

        return `
            <div class="form-config-box">
                <form-input name="cfg-allow_origin" label="Allow Origin" value="${config.allow_origin || '*'}" placeholder="*"></form-input>
                
                <div class="form-field-group">
                    <label class="form-label">Allow Methods</label>
                    <div class="checkbox-grid">
                        ${potentialMethods.map(m => `
                            <label class="checkbox-label">
                                <input type="checkbox" name="cfg-method" value="${m}" ${methods.includes(m) ? 'checked' : ''}>
                                ${m}
                            </label>
                        `).join('')}
                    </div>
                </div>

                <form-input name="cfg-allow_headers" label="Allow Headers" value="${headers.join(', ')}" placeholder="Content-Type, Authorization"></form-input>

                <form-toggle name="cfg-allow_credentials" label="Allow Credentials" ${config.allow_credentials ? 'checked' : ''}></form-toggle>
                
                <form-input name="cfg-max_age" label="Max Age (Seconds)" type="number" value="${config.max_age || 3600}"></form-input>
            </div>
        `;
    }

    private static renderApiKeyForm(config: any): string {
        return `
            <div class="form-config-box">
                <form-input name="cfg-header_name" label="Header Name" value="${config.header_name || 'X-API-Key'}"></form-input>
                <div class="form-field-group">
                    <label class="form-label">Valid Keys</label>
                    <div class="help-text">One key per line</div>
                    <textarea class="form-textarea" name="cfg-keys" style="height: 150px;">${(config.keys || []).join('\n')}</textarea>
                </div>
            </div>
        `;
    }

    private static renderIpFilterForm(config: any): string {
        return `
            <div class="form-config-box">
                <div class="form-field-group">
                    <label class="form-label">Whitelist</label>
                    <div class="help-text">One per line (IP or CIDR)</div>
                    <textarea class="form-textarea" name="cfg-whitelist" style="height: 120px;">${(config.whitelist || []).join('\n')}</textarea>
                </div>
                <div class="form-field-group">
                    <label class="form-label">Blacklist</label>
                    <div class="help-text">One per line (IP or CIDR)</div>
                    <textarea class="form-textarea" name="cfg-blacklist" style="height: 120px;">${(config.blacklist || []).join('\n')}</textarea>
                </div>
            </div>
        `;
    }

    private static renderPayloadFilterForm(config: any): string {
        return `
            <div class="form-config-box">
                <form-input type="number" name="cfg-max_size_bytes" label="Max Size (Bytes)" value="${config.max_size_bytes || 1048576}"></form-input>
                <div class="help-text">Default is 1MB (1,048,576 bytes)</div>
            </div>
        `;
    }

    private static renderMockForm(config: any): string {
        return `
            <div class="form-config-box">
                <form-input type="number" name="cfg-status" label="Response Status" value="${config.status || 200}"></form-input>
                
                <div class="form-field-group">
                    <label class="form-label">Response Body (JSON)</label>
                    <textarea class="form-textarea" name="cfg-body" style="height: 200px;">${typeof config.body === 'object' ? JSON.stringify(config.body, null, 2) : (config.body || '')}</textarea>
                </div>

                <div class="form-field-group">
                    <label class="form-label">Headers (JSON)</label>
                    <textarea class="form-textarea" name="cfg-headers" style="height: 100px;">${JSON.stringify(config.headers || { "Content-Type": "application/json" }, null, 2)}</textarea>
                </div>
            </div>
        `;
    }

    static getConfigFromForm(shadow: ShadowRoot, type: string): any {
        const config: any = {};
        shadow.querySelectorAll('[name^="cfg-"]').forEach((el: any) => {
            const name = el.getAttribute('name');
            if (!name || name === 'cfg-method') return;

            const key = name.replace('cfg-', '');
            let val = el.value;
            
            if (el.tagName === 'FORM-TOGGLE') val = el.checked;
            if (el.getAttribute('type') === 'number') val = parseInt(val, 10);

            if (['keys', 'whitelist', 'blacklist'].includes(key)) {
                val = (val || '').split('\n').map((s: string) => s.trim()).filter((s: string) => s !== '');
            }

            if (key === 'allow_headers') {
                val = (val || '').split(',').map((s: string) => s.trim()).filter((s: string) => s !== '');
            }

            if (key === 'body' || key === 'headers') {
                try {
                    val = JSON.parse(val);
                } catch (e) {
                    // keep as string if not valid JSON
                }
            }

            config[key] = val;
        });

        if (type === 'cors') {
            const checkedMethods = Array.from(shadow.querySelectorAll('input[name="cfg-method"]:checked')) as HTMLInputElement[];
            config.allow_methods = checkedMethods.map(m => m.value);
        }

        return config;
    }
    static attachEvents(container: HTMLElement, type: string, onChange: (config: any) => void) {
        const inputs = container.querySelectorAll('input, textarea, select, form-input, form-toggle');
        const shadow = container.getRootNode() as ShadowRoot;
        
        inputs.forEach(input => {
            const handler = () => {
                const config = this.getConfigFromForm(shadow, type);
                onChange(config);
            };
            input.addEventListener('change', handler);
            input.addEventListener('input', handler);
        });
    }
}
